import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router'
import { LogOut, Menu, Monitor, Moon, RefreshCw, Search, Sun } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { errorMessage } from '@/lib/api'
import { logout } from '@/lib/firebase'
import { useRefreshAll } from '@/lib/queries'
import { useAccess } from '@/providers/access'
import { useTheme, type Theme } from '@/providers/theme-provider'
import { CommandPalette } from './command-palette'
import { nav } from './nav'

function Rail({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useAccess()
  const { theme, setTheme } = useTheme()
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-14 items-center gap-2 px-5">
        <Logo mono className="h-5 text-sidebar-accent-foreground" />
        <span className="rounded bg-sidebar-accent px-1.5 py-0.5 text-[0.7rem] font-medium text-sidebar-muted">admin</span>
      </div>

      <nav className="flex flex-col gap-0.5 px-3 pt-2" aria-label="Main">
        {nav.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'group relative flex h-9 items-center gap-2.5 rounded-lg px-3 font-medium text-sidebar-foreground/85 hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground',
                isActive && 'bg-sidebar-accent text-sidebar-accent-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* a lit window marks where you are */}
                <span className={cn('absolute top-1/2 left-0 h-3.5 w-0.5 -translate-y-1/2 rounded-full bg-window opacity-0', isActive && 'opacity-100')} aria-hidden />
                <Icon className="size-4 shrink-0" aria-hidden />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto space-y-2 border-t border-sidebar-border p-3">
        {me.mode === 'open' && (
          <p className="rounded-lg bg-window/10 px-3 py-2 text-xs leading-snug text-window">
            Open access: any signed-in user can see this. Testing only.
          </p>
        )}
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1 px-2 leading-tight">
            <div className="truncate text-xs text-sidebar-muted">Signed in as</div>
            <div className="truncate text-[0.8rem] text-sidebar-accent-foreground">{me.email || me.uid}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" aria-label="Theme">
                <ThemeIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top">
              {([['light', Sun, 'Light'], ['dark', Moon, 'Dark'], ['system', Monitor, 'Match system']] as [Theme, typeof Sun, string][]).map(([value, Icon, label]) => (
                <DropdownMenuItem key={value} onSelect={() => setTheme(value)}>
                  <Icon /> {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon-sm" className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" onClick={() => logout()} aria-label="Sign out">
            <LogOut />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const refreshAll = useRefreshAll()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const refresh = async () => {
    setRefreshing(true)
    try {
      await refreshAll()
      toast.success('Reloaded from Firebase and the database')
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="min-h-svh">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 lg:block">
        <Rail />
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-64 gap-0 border-0 bg-sidebar p-0 sm:max-w-64" showCloseButton={false}>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Rail onNavigate={() => setMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-background/85 px-4 backdrop-blur sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu />
          </Button>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex h-8 w-full max-w-md items-center gap-2 rounded-lg border border-input bg-card px-2.5 text-left text-muted-foreground hover:border-primary/40"
          >
            <Search className="size-4" aria-hidden />
            <span className="flex-1 truncate">Search teams and people</span>
            <kbd className="hidden rounded border bg-muted px-1.5 font-sans text-[0.7rem] sm:block">⌘K</kbd>
          </button>
          <Button variant="ghost" size="icon" className="ml-auto" onClick={refresh} disabled={refreshing} aria-label="Reload data" title="Reload data from Firebase and the database">
            <RefreshCw className={cn(refreshing && 'animate-spin')} />
          </Button>
        </header>

        <main className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  )
}
